import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class CreateContentSurveyReportParams {
  @IsUUID()
  @IsNotEmpty()
  content_survey_id: string;
}

export class GetContentSurveyResultParams {
  @IsUUID()
  @IsNotEmpty()
  content_survey_id: string;
}

export class ContentSurveyAnswerParams {
  @IsUUID()
  @IsNotEmpty()
  content_survey_id: string;
}

export class ResizeImageParams {
  @IsUUID()
  @IsNotEmpty()
  attachment_id: string;
}

export class GetAttachmentUrlParams {
  @IsUUID()
  @IsNotEmpty()
  attachment_id: string;
}

export class GetTagsQuery {
  @IsString()
  @IsOptional()
  search?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  tag_ids?: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  account_ids?: string[];

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class ResizeImageQuery {
  @IsInt()
  @IsOptional()
  width?: number;

  @IsInt()
  @IsOptional()
  height?: number;
}

export class AsyncDownloadResponse {
  @IsUUID()
  @IsNotEmpty()
  download_id: string;
}

export class ContentSurveyResultActions {
  @IsBoolean()
  @IsNotEmpty()
  report: boolean;
}

export class UserFreeTextStatistics {
  @IsIn(['free_text'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'free_text';

  @IsString()
  @IsOptional()
  user_answer?: string;

  @IsInt()
  @IsNotEmpty()
  total_answers: number;
}

export class UserOtherValueStatistics {
  @IsInt()
  @IsNotEmpty()
  answer_count: number;

  @IsInt()
  @IsNotEmpty()
  percentage: number;

  @IsBoolean()
  @IsNotEmpty()
  is_selected: boolean;
}

export class UserChoiceAnswerOptionStatistic {
  @IsUUID()
  @IsOptional()
  id?: string;

  @IsIn(['text', 'image', 'text_image'])
  @IsString()
  @IsOptional()
  type?: 'text' | 'image' | 'text_image';

  @IsString()
  @IsOptional()
  text_value?: string;

  @IsString()
  @IsOptional()
  image_value_url?: string;

  @IsString()
  @IsOptional()
  image_caption?: string;

  @IsBoolean()
  @IsNotEmpty()
  is_edited: boolean;

  @IsInt()
  @IsNotEmpty()
  answer_count: number;

  @IsInt()
  @IsNotEmpty()
  percentage: number;

  @IsBoolean()
  @IsNotEmpty()
  is_selected: boolean;
}

export class UserMultipleChoiceStatistics {
  @IsIn(['multiple_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'multiple_choice';

  @IsInt()
  @IsNotEmpty()
  total_answers: number;

  @ValidateNested({ each: true })
  @Type(() => UserChoiceAnswerOptionStatistic)
  @IsArray()
  @IsNotEmpty()
  options: UserChoiceAnswerOptionStatistic[];

  @ValidateNested()
  @Type(() => UserOtherValueStatistics)
  @IsOptional()
  other_value_stats?: UserOtherValueStatistics;
}

export class UserSingleChoiceStatistics {
  @IsIn(['single_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'single_choice';

  @IsInt()
  @IsNotEmpty()
  total_answers: number;

  @ValidateNested({ each: true })
  @Type(() => UserChoiceAnswerOptionStatistic)
  @IsArray()
  @IsNotEmpty()
  options: UserChoiceAnswerOptionStatistic[];

  @ValidateNested()
  @Type(() => UserOtherValueStatistics)
  @IsOptional()
  other_value_stats?: UserOtherValueStatistics;
}

export class ContentSurveyQuestionStatistics {
  @IsNotEmpty()
  object:
    | UserSingleChoiceStatistics
    | UserMultipleChoiceStatistics
    | UserFreeTextStatistics;
}

export type AttachmentItemType = string;

export class AttachmentItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['image', 'file'])
  @IsString()
  @IsNotEmpty()
  type: 'image' | 'file';
}

export class BaseContentItem {
  @IsString()
  @IsOptional()
  data?: string;

  @ValidateNested({ each: true })
  @Type(() => AttachmentItem)
  @IsArray()
  @IsOptional()
  attachments?: AttachmentItem[];
}

export class ContentSurveyQuestionResult {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => BaseContentItem)
  @IsNotEmpty()
  content: BaseContentItem;

  @ValidateNested()
  @Type(() => ContentSurveyQuestionStatistics)
  @IsNotEmpty()
  statistics: ContentSurveyQuestionStatistics;

  @IsBoolean()
  @IsNotEmpty()
  skipped: boolean;
}

export class ContentSurveyResultResponse {
  @IsString()
  @IsNotEmpty()
  title: string;

  @IsBoolean()
  @IsNotEmpty()
  is_anonymous: boolean;

  @IsBoolean()
  @IsNotEmpty()
  show_result: boolean;

  @IsDateString()
  @IsOptional()
  completed_at?: string;

  @IsInt()
  @IsNotEmpty()
  passes_num: number;

  @ValidateNested({ each: true })
  @Type(() => ContentSurveyQuestionResult)
  @IsArray()
  @IsNotEmpty()
  questions: ContentSurveyQuestionResult[];

  @ValidateNested()
  @Type(() => ContentSurveyResultActions)
  @IsNotEmpty()
  actions: ContentSurveyResultActions;
}

export class ContentSurveyActions {
  @IsBoolean()
  @IsNotEmpty()
  results: boolean;

  @IsBoolean()
  @IsNotEmpty()
  report: boolean;
}

export class UserAnswerMultipleTextChoice {
  @IsIn(['multiple_text_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'multiple_text_choice';

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  selected_options: string[];
}

export class UserAnswerSingleTextChoice {
  @IsIn(['single_text_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'single_text_choice';

  @IsUUID()
  @IsOptional()
  selected_option?: string;
}

export class UserAnswerFreeText {
  @IsIn(['free_text'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'free_text';

  @IsString()
  @IsNotEmpty()
  text: string;
}

export class ContentSurveyUserAnswerOneOf {
  @IsNotEmpty()
  object:
    | UserAnswerFreeText
    | UserAnswerSingleTextChoice
    | UserAnswerMultipleTextChoice;
}

export class ContentSurveyUserAnswer {
  @IsUUID()
  @IsNotEmpty()
  question_id: string;

  @ValidateNested()
  @Type(() => ContentSurveyUserAnswerOneOf)
  @IsOptional()
  user_answer?: ContentSurveyUserAnswerOneOf;
}

export class ContentSurveyUserPassing {
  @ValidateNested({ each: true })
  @Type(() => ContentSurveyUserAnswer)
  @IsArray()
  @IsNotEmpty()
  answers: ContentSurveyUserAnswer[];

  @IsInt()
  @IsNotEmpty()
  version: number;
}

export class FreeTextAnswerSettings {
  @IsIn(['free_text'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'free_text';

  @IsInt()
  @IsOptional()
  max_length?: number;

  @IsInt()
  @IsOptional()
  min_length?: number;
}

export class TextChoiceAnswerOption {
  @IsUUID()
  @IsOptional()
  id?: string;

  @IsIn(['text'])
  @IsString()
  @IsNotEmpty()
  type: 'text';

  @IsString()
  @IsNotEmpty()
  text_value: string;
}

export class MultipleTextChoiceAnswerSettings {
  @IsIn(['multiple_text_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'multiple_text_choice';

  @ValidateNested({ each: true })
  @Type(() => TextChoiceAnswerOption)
  @IsArray()
  @IsNotEmpty()
  answer_options: TextChoiceAnswerOption[];
}

export class SingleTextChoiceAnswerSettings {
  @IsIn(['single_text_choice'])
  @IsString()
  @IsNotEmpty()
  answer_type: 'single_text_choice';

  @ValidateNested({ each: true })
  @Type(() => TextChoiceAnswerOption)
  @IsArray()
  @IsNotEmpty()
  answer_options: TextChoiceAnswerOption[];
}

export class ContentSurveyQuestionAnswerSettings {
  @IsNotEmpty()
  object:
    | SingleTextChoiceAnswerSettings
    | MultipleTextChoiceAnswerSettings
    | FreeTextAnswerSettings;
}

export class ContentSurveyQuestion {
  @IsUUID()
  @IsOptional()
  id?: string;

  @ValidateNested()
  @Type(() => BaseContentItem)
  @IsNotEmpty()
  content: BaseContentItem;

  @ValidateNested()
  @Type(() => ContentSurveyQuestionAnswerSettings)
  @IsNotEmpty()
  answer_settings: ContentSurveyQuestionAnswerSettings;
}

export class ContentSurvey {
  @IsUUID()
  @IsOptional()
  id?: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsBoolean()
  @IsNotEmpty()
  anonymous: boolean;

  @IsBoolean()
  @IsNotEmpty()
  show_result: boolean;

  @IsDateString()
  @IsOptional()
  completed_at?: string;

  @IsInt()
  @IsNotEmpty()
  version: number;

  @ValidateNested({ each: true })
  @Type(() => ContentSurveyQuestion)
  @IsArray()
  @IsNotEmpty()
  questions: ContentSurveyQuestion[];
}

export class ContentSurveyItem {
  @ValidateNested()
  @Type(() => ContentSurvey)
  @IsNotEmpty()
  survey: ContentSurvey;

  @IsInt()
  @IsOptional()
  passes_num?: number;

  @ValidateNested()
  @Type(() => ContentSurveyUserPassing)
  @IsOptional()
  passing?: ContentSurveyUserPassing;

  @ValidateNested()
  @Type(() => ContentSurveyActions)
  @IsOptional()
  actions?: ContentSurveyActions;
}

export class Tag {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class TagsResponse {
  @ValidateNested({ each: true })
  @Type(() => Tag)
  @IsArray()
  @IsNotEmpty()
  tags: Tag[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;
}

export class AttachmentUrl {
  @IsString()
  @IsNotEmpty()
  url: string;
}

export class ContentSurveyAnswerRequest {
  @ValidateNested({ each: true })
  @Type(() => ContentSurveyUserAnswer)
  @IsArray()
  @IsNotEmpty()
  answers: ContentSurveyUserAnswer[];

  @IsInt()
  @IsNotEmpty()
  version: number;
}

export class UploadAttachmentRequest {
  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @Type(() => String)
  @IsNotEmpty()
  file: File;

  @IsIn(['image', 'file'])
  @IsString()
  @IsNotEmpty()
  type: 'image' | 'file';
}

@injectable()
export class ContentService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async createContentSurveyReport({
    params,
  }: {
    params: CreateContentSurveyReportParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, CreateContentSurveyReportParams),
    ]);

    let requestPath = '/content/survey/{content_survey_id}/report';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof CreateContentSurveyReportParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      AsyncDownloadResponse,
    );

    return response;
  }

  async getContentSurveyResult({
    params,
  }: {
    params: GetContentSurveyResultParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetContentSurveyResultParams),
    ]);

    let requestPath = '/content/survey/{content_survey_id}/result';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetContentSurveyResultParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      ContentSurveyResultResponse,
    );

    return response;
  }

  async contentSurveyAnswer({
    params,
    body,
  }: {
    params: ContentSurveyAnswerParams;
    body: ContentSurveyAnswerRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, ContentSurveyAnswerParams),
      this.validator.validateOrReject(body, ContentSurveyAnswerRequest),
    ]);

    let requestPath = '/content/survey/{content_survey_id}/answer';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof ContentSurveyAnswerParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      ContentSurveyItem,
    );

    return response;
  }

  async getTags({
    query = {},
  }: {
    query?: GetTagsQuery;
  } = {}) {
    await Promise.all([this.validator.validateOrReject(query, GetTagsQuery)]);

    let requestPath = '/content/tags';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(result, TagsResponse);

    return response;
  }

  async resizeImage({
    query = {},
    params,
  }: {
    query?: ResizeImageQuery;
    params: ResizeImageParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, ResizeImageQuery),
      this.validator.validateOrReject(params, ResizeImageParams),
    ]);

    let requestPath = '/attachment/{attachment_id}/image/resize';

    const paramsKeys = Object.keys(params) as (keyof ResizeImageParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(
      requestPath,
      {
        withAuth: true,
        withSide: false,
        isJson: true,
      },
      query,
    );

    return result;
  }

  async getAttachmentUrl({ params }: { params: GetAttachmentUrlParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetAttachmentUrlParams),
    ]);

    let requestPath = '/attachment/{attachment_id}/url';

    const paramsKeys = Object.keys(params) as (keyof GetAttachmentUrlParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(result, AttachmentUrl);

    return response;
  }

  async uploadAttachment({ body }: { body: UploadAttachmentRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, UploadAttachmentRequest),
    ]);

    let requestPath = '/attachment/upload';

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    const response = await this.unmarshaller.unmarshall(result, AttachmentItem);

    return response;
  }
}
